import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {forkJoin, Observable, of, throwError} from 'rxjs';
import {IAppSettingsWrap, IBusinessDomainCollection, ICredentialsWrap, Id, IOrderForm, IStationGeoIp, IStationLogRequest, IStationSocketNamesReply, IStationStatsPropsV1V2, IStationStatsPropsV2, IStationStatsV1, IStationStatsV1V2, IUser} from "../../../interfaces";
import {IEnterpriseReseller, IEnterpriseWhiteLabelUpdate, IPrivileges} from "../../../interfaces/enterprise-interface";
import {IAccountDetails} from "../../../interfaces/enterprise-account-interface";
import {retryWithBackoff} from "../../../../libs/rxjs/retryWithBackoff";
import {settleAll, settleAllProgressive} from "../../../../libs/rxjs/settleAll";
import {IBusinessAccount, IEnterpriseBusinesses} from "../../../interfaces/enterprise-businesses-interface";
import {IEnterpriseApps, IEnterpriseBusinessUsers, IEnterpriseBusinessUserUpdate, IEnterprisePrivilegesUpdate} from "../../../interfaces/enterprise-business-users-interface";
import {catchError, finalize, map, switchMap, take} from "rxjs/operators";
import {ToastrService} from "ngx-toastr";
import {TranslocoService} from "@ngneat/transloco";
import {compareIds, concatIdBusinessId, dateUnixToString, dateUnixToStringNoReduce, DEFAULT_TEMPLATE_ID, getCredentialsHeader, isNullOrUndefOrEmpty} from "../../../common";
import {AppService, StoreSelectorsService} from "../../../services";
import {Compbaser} from "../../../components/compbaser";
import {Lib} from "../../../../libs/Lib";
import * as xml2js from 'xml2js';
import * as _ from 'lodash';

const httpOptions = {headers: new HttpHeaders({'Content-Type': 'application/json'}), responseType: 'text' as 'json'};

const chunkArrayInGroups = (arr, size) => {
    const result = [];
    for (let i = 0; i < arr.length; i += size)
        result.push(arr.slice(i, i + size));
    return result;
};

@Injectable()
export class EnterpriseService extends Compbaser {
    private coreAppData: IAppSettingsWrap;

    constructor(private translocoService: TranslocoService,
                private _http: HttpClient,
                private appService: AppService,
                private storeSelector: StoreSelectorsService,
                private toast: ToastrService) {
        super();
        this.cod(
            this.storeSelector.getCoreData()
                .subscribe((i_coreData: IAppSettingsWrap) => this.coreAppData = i_coreData)
        );
    }

    private onError(i_msg) {
        console.log('error ' + i_msg);
        this.toast.error(i_msg);
    }

    private processStationTime(value): string {
        if (isNullOrUndefOrEmpty(value)) return '';
        if (String(value).length > 10) {
            return dateUnixToString(value, -3, 'MM-DD-YY HH:mm:ss');
        } else if (String(value).length > 7) {
            return dateUnixToStringNoReduce(value, 'MM-DD-YY HH:mm:ss');
        }
        return '';
    }

    getEnterpriseReseller(i_command): Observable<IEnterpriseReseller | IEnterpriseBusinesses | IAccountDetails> {
        const url = `${this.coreAppData[0].webApiResellerBaseUrl}&command=${i_command}`;
        let enterpriseReply;
        return this._http.get<IEnterpriseReseller>(url, httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map(v => {
                xml2js.parseString(v, (err, i_reseller) => {
                    if (err) {
                        console.error('getEnterpriseReseller xml conversion error');
                        this.onError(this.translocoService.translate('toast.sorryEncounteredProblem', {}, this.translocoService.getActiveLang()));
                    }
                    enterpriseReply = i_reseller;
                });
                return enterpriseReply;
            })
        );
    }

    addEnterpriseBusinessUser(action: IEnterpriseBusinessUserUpdate): Observable<any> {
        const url = `${this.coreAppData[0].webApiResellerBaseUrl}&command=AddBusinessUser&businessId=${action.businessId}&newUserName=${action.name2}&newUserPassword=${Lib.Base64().encode(action.password)}&privilegeId=${action.privilegeId}&accessMask=${action.computedAccessMask}`;
        console.log(url);
        return this._http.get<any>(url, httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((result: any) => result)
        );
    }

    removeBusinessUser(businessUserName): Observable<string> {
        const url = `${this.coreAppData[0].webApiResellerBaseUrl}&command=RemoveBusinessUser&customerUserName=${businessUserName}`;
        return this._http.get<any>(url, httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((result: any) => result)
        );
    }

    removeBusinessAccount(businessId): Observable<string> {
        const url = `${this.coreAppData[0].webApiResellerBaseUrl}&command=DeleteAccount&buinessId=${businessId}`;
        return this._http.get<any>(url, httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((result: any) => result)
        );
    }

    removePrivileges(enterprisePrivilegesUpdate: IEnterprisePrivilegesUpdate, enterpriseBusinessUsers: IEnterpriseBusinessUsers): Observable<string> {
        const observables = [];
        enterpriseBusinessUsers.BusinessUsers.forEach((user) => {
            if (user.$.privilegeId === enterprisePrivilegesUpdate.privilegeId) {
                const url = `${this.coreAppData[0].webApiResellerBaseUrl}&command=UpdateUserPrivilege&privilegeId=${enterprisePrivilegesUpdate.privilegeDefaultId}&accessMask=${user.$.accessMask}&customerUserName=${user.$.name}`;
                observables.push(this._http.get(url, httpOptions).pipe(
                    retryWithBackoff(url, this.storeSelector, this.toast),
                    catchError((e) => {
                        console.error('Problem changing privileges for users ' + e.toString());
                        this.onError(this.translocoService.translate('toast.sorryEncounteredProblem', {}, this.translocoService.getActiveLang()));
                        return e;
                    }),
                    map(result => result)));
            }
        });
        if (observables.length === 0) observables.push(['']);
        return forkJoin(observables).pipe(
            switchMap((res) => {
                const url = `${this.coreAppData[0].webApiResellerBaseUrl}&command=DeletePrivilege&privilegeId=${enterprisePrivilegesUpdate.privilegeId}`;
                return this._http.get<any>(url, httpOptions).pipe(
                    retryWithBackoff(url, this.storeSelector, this.toast),
                    map((result: any) => result)
                );
            }));
    }

    renameBusinessUser(userName: string, newUserName: string): Observable<string> {
        const url = `${this.coreAppData[0].webApiResellerBaseUrl}&command=RenameBusinessUser&userName=${userName}&newUserName=${newUserName}`;
        return this._http.get<any>(url, httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((result: any) => {
                return result;
            })
        );
    }

    renameResellerUser(userName: string, newUserName: string): Observable<boolean> {
        const url = `${this.coreAppData[0].webApiResellerBaseUrl}&command=RenameReseller&userName=${userName}&newUserName=${newUserName}`;
        console.log(url);
        return this._http.get<any>(url, httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((result: any) => {
                return result;
            })
        );
    }

    updateBusinessUserPriv(privilegeId, accessMask, name): Observable<string> {
        const url = `${this.coreAppData[0].webApiResellerBaseUrl}&command=UpdateUserPrivilege&privilegeId=${privilegeId}&accessMask=${accessMask}&customerUserName=${name}`;
        return this._http.get<any>(url, httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((result: any) => result)
        );
    }

    // per-user migration permission (single flag gating migrate + revert)
    updateUserAllowMigration(name, allowMigration: boolean): Observable<string> {
        const url = `${this.coreAppData[0].webApiResellerBaseUrl}&command=SetUserAllowMigration&customerUserName=${name}&allowMigration=${allowMigration ? '1' : '0'}`;
        return this._http.get<any>(url, httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((result: any) => result)
        );
    }

    importAccount(action: IEnterpriseBusinessUserUpdate): Observable<string> {
        const url = `${this.coreAppData[0].webApiResellerBaseUrl}&command=AssociateAccount&customerUserName=${action.name2}&customerPassword=${Lib.Base64().encode(action.password)}`;
        return this._http.get<any>(url, httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((result: any) => result)
        );
    }

    public savePrivileges(i_privileges: Array<IPrivileges>, privelegesId: string, privelegesName: string): Observable<any> {
        let httpPost;
        Lib.PrivilegesXmlTemplate(false, privelegesId, i_privileges, (err, template) => {
            template = template.replace(/>\s*/g, '>').replace(/\s*</g, '<').replace(/(\r\n|\n|\r)/gm, "");
            template = template.replace(/<Privilege>/g, '').replace(/<\/Privilege>/g, '');
            const url = `${this.coreAppData[0].webApiResellerBaseUrl}&command=UpdatePrivilege&privilegeName=${privelegesName}&privilegeId=${privelegesId}`;
            httpPost = this._http.post(url, template).pipe(
                retryWithBackoff(url, this.storeSelector, this.toast),
                catchError((err2) => {
                    console.error('Error saving privileges ' + err2);
                    this.onError(this.translocoService.translate('toast.sorryEncounteredProblem', {}, this.translocoService.getActiveLang()));
                    return throwError(err2);
                }),
                finalize(() => {
                }),
                map((v) => {
                    return 'true';
                }));
        });
        return httpPost;
    }

    addPrivileges(action: IEnterprisePrivilegesUpdate, privileges: IPrivileges[]): Observable<any> {
        let httpPost;
        Lib.PrivilegesXmlTemplate(true, action.privilegeDefaultId, privileges, (err, template) => {
            if (err) return throwError(err);
            template = template.replace(/>\s*/g, '>').replace(/\s*</g, '<').replace(/(\r\n|\n|\r)/gm, "");
            template = template.replace(/<Privilege>/g, '').replace(/<\/Privilege>/g, '');
            const url = `${this.coreAppData[0].webApiResellerBaseUrl}&command=AddPrivilege&privilegeName=${action.selPrivName}`;
            httpPost = this._http.post(url, template).pipe(
                retryWithBackoff(url, this.storeSelector, this.toast),
                catchError((err2) => {
                    console.error('Error saving privileges ' + err2);
                    this.onError(this.translocoService.translate('toast.sorryEncounteredProblem', {}, this.translocoService.getActiveLang()));
                    return throwError(err2);
                }),
                finalize(() => {
                }),
                map((privilegeId) => {
                    let newPrivileges: IPrivileges;
                    Lib.PrivilegesXmlTemplate(true, action.privilegeDefaultId, null, (err2, template2) => {
                        if (err2) return throwError(err2);
                        newPrivileges = {
                            $: {id: String(privilegeId), name: action.selPrivName},
                            Groups: [{
                                Group: template2.Privilege.Groups[0].Group
                            }]
                        };
                    });
                    return newPrivileges;
                }));
        });
        return httpPost;
    }

    updateDefaultPrivileges(action: IEnterprisePrivilegesUpdate): Observable<string> {
        const url = `${this.coreAppData[0].webApiResellerBaseUrl}&command=SetPrivilegeAsDefault&privilegeId=${action.privilegeId}`;
        return this._http.get<any>(url, httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((result: any) => result)
        );
    }

    duplicateBusinessUserPriv(action: IEnterpriseBusinessUserUpdate): Observable<string> {
        const templateFromBusinessId = isNullOrUndefOrEmpty(action.businessId) === false && !compareIds(action.businessId, '-1') ? action.businessId : DEFAULT_TEMPLATE_ID;
        const url = `${this.coreAppData[0].webApiResellerBaseUrl}&command=DuplicateAccount&customerBusinessName=${action.name1}&customerUserName=${action.name2}&customerPassword=${Lib.Base64().encode(action.password)}&templateBusinessId=${templateFromBusinessId}&privilegeId=${action.privilegeId}&accessMask=${action.computedAccessMask}`;
        return this._http.get<any>(url, httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((result: any) => result)
        );
    }

    updateAccount(action: IBusinessAccount): Observable<string> {
        const url = `${this.coreAppData[0].webApiResellerBaseUrl}&command=UpdateAccount&buinessId=${action.$.businessId}&businessName=${action.$.name}&businessDescription=${action.$.businessDescription}&maxMonitors=${action.$.maxMonitors}&allowSharing=${action.$.allowSharing}`;
        return this._http.get<any>(url, httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((result: any) => result)
        );
    }

    updateApps(action: IEnterpriseApps): Observable<string> {
        let url;
        if (action.installed === '1') {
            url = `${this.coreAppData[0].webApiResellerBaseUrl}&command=InstallApp&appId=${action.id}`;
        } else {
            url = `${this.coreAppData[0].webApiResellerBaseUrl}&command=UninstallApp&appId=${action.id}`;
        }
        return this._http.get<any>(url, httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((result: any) => result)
        );
    }

    getEnterpriseBusinessUsers(i_businesses: Array<IBusinessAccount>): Observable<IEnterpriseBusinessUsers> {
        if (isNullOrUndefOrEmpty(i_businesses) || i_businesses.length === 0)
            i_businesses = [];
        const businessIds = i_businesses.map((v) => v.$.businessId);
        const businessesArray = chunkArrayInGroups(businessIds, 200);
        const observables: Array<Observable<any>> = [];
        businessesArray.forEach((member) => {
            const chunkBusinessIds = member.join('.');
            const url = `${this.coreAppData[0].webApiResellerBaseUrl}&command=GetBusinessUsers&businessList=${chunkBusinessIds}`;
            observables.push(this._http.get(url, httpOptions).pipe(
                retryWithBackoff(url, this.storeSelector, this.toast),
                catchError((e) => {
                    console.error('Problem loading users ' + e.toString());
                    this.onError(this.translocoService.translate('toast.sorryEncounteredProblem', {}, this.translocoService.getActiveLang()));
                    return e;
                }), map(result => result)));
        });
        return forkJoin(observables).pipe(
            map((result) => {
                const users: IEnterpriseBusinessUsers = {BusinessUsers: []};
                result.forEach((m) => {
                    xml2js.parseString(m, (err, i_businessUsers) => {
                        if (err) {
                            console.error('getEnterpriseBusinessUsers xml conversion error');
                            this.onError(this.translocoService.translate('toast.sorryEncounteredProblem', {}, this.translocoService.getActiveLang()));
                        }
                        users.BusinessUsers.push(...i_businessUsers.Users.User);
                    });
                });
                return users;
            }));
    }

    public getEnterpriseStationSocketNames(config: IBusinessDomainCollection): Observable<Array<IStationStatsV1V2>> {
        const observables: Array<Observable<any>> = [];
        // tslint:disable-next-line:forin
        for (const source in config) {
            const i_businesses = config[source];
            if (source.indexOf('mars') > -1 || source.indexOf('mercury') > -1) continue;
            const a = chunkArrayInGroups(i_businesses, 100);
            // tslint:disable-next-line:forin
            for (const v in a) {
                const t = a[v];
                const businesses = t.join(',');
                const url = `https://${source}/Web/getStationsInfo.ashx?businessList=[${businesses}]`;
                observables.push(
                    this._http.get(url, {headers: new HttpHeaders({'Content-Type': 'application/json', 'Accept': 'text/xml'}), responseType: 'json'}
                    ).pipe(
                        map(jData => {
                            return {jData, source};
                        })
                    )
                );
            }
        }
        /** settleAll instead of forkJoin: render whatever chunks come back, never hang on a stalled chunk **/
        return settleAll(observables, {label: 'stationNames'}).pipe(
            map((val) => {
                const stations: any = [];
                val.forEach((v) => {
                    const source = v.source;
                    if (v.jData) {
                        v.jData.forEach((station: IStationSocketNamesReply) => {
                            const stationData: IStationStatsV1V2 = {
                                $: {
                                    station_business_id: concatIdBusinessId(station.station_id, station.business_id, 2),
                                    id: station.station_id,
                                    businessId: station.business_id,
                                    name: station.station_name,
                                    source,
                                    wdConnected: '',
                                    wdNative: '',
                                    wdExtension: '',
                                    libVersion: '',
                                    socket: '0',
                                    connected: '0',
                                    status: '0',
                                    version: '2',
                                    platform: '',
                                    playerId: '',
                                    totalMemory: '',
                                    userHostAddress: '',
                                    watchDogConnection: '',
                                    runningTime: '',
                                    os: '',
                                    caching: '',
                                    lastUpdate: '',
                                    peakMemory: '',
                                    appVersion: '',
                                    airVersion: '',
                                    localAddress: '',
                                    connection: '',
                                    cameraStatus: '',
                                    lastCameraTest: '',
                                    localIp: '',
                                    publicIp: '',
                                    playing: '',
                                    startTime: '',
                                    tree_path: '',
                                    geoLocation: null
                                }
                            };
                            stations.push(stationData);
                        });
                    }
                });
                return stations;
            }));
    }

    public getEnterpriseStations(config: IBusinessDomainCollection): Observable<{ stations: Array<IStationStatsV1>, isFinal: boolean }> {
        const observables: Array<Observable<any>> = [];
        // Legacy stations
        for (const source in config) {
            const i_businesses = config[source];
            if (source.indexOf('mars') > -1 || source.indexOf('mercury') > -1) continue;
            const a = chunkArrayInGroups(i_businesses, 100);
            // tslint:disable-next-line:forin
            for (const v in a) {
                const t = a[v];
                const businesses = t.join(',');
                const url = `https://${source}/WebService/StationService.asmx/getSocketStatusList?i_businessList=${businesses}`;
                observables.push(
                    this._http.get(url, {headers: new HttpHeaders({'Content-Type': 'text/xml', 'Accept': 'text/xml'}), responseType: 'text' as 'json'}
                    ).pipe(
                        map(xml => ({xml, source}))
                    ));
            }
        }
        // Socket stations
        for (const source in config) {
            const i_businesses = config[source];
            if (source.indexOf('mars') > -1 || source.indexOf('mercury') > -1) continue;
            const a = chunkArrayInGroups(i_businesses, 100);
            // tslint:disable-next-line:forin
            for (const v in a) {
                const t = a[v];
                const businesses = t.join(',');
                const url = `https://${source}/Web/getSocketStatusList.ashx?businessList=[${businesses}]`;
                observables.push(
                    this._http.get(url, {headers: new HttpHeaders({'Content-Type': 'application/json', 'Accept': 'text/xml'}), responseType: 'json'}
                    ).pipe(
                        map((jData: IStationStatsPropsV1V2[]) => {
                            const stations: IStationStatsPropsV1V2[] = [];
                            jData.forEach((i_station) => {
                                i_station.version = '2';
                                i_station.station_business_id = concatIdBusinessId(i_station.stationId, i_station.businessId, '2');
                                i_station.wdConnected = i_station.wdConnected || '0';
                                i_station.wdExtension = i_station.wdExtension || '0';
                                i_station.wdNative = i_station.wdNative || '0';
                                stations.push(i_station);
                            });
                            return {jData: stations, source};
                        })
                    )
                );
            }
        }
        /** settleAllProgressive instead of forkJoin: a stalled or timed out chunk no longer blocks
         *  the entire station list. progress.results is CUMULATIVE so each emission paints the
         *  stations from the chunks that have come back so far, the spinner stops on the first one.
         *  isFinal (progress.done) tells the reducer when it is safe to mark missing v2 stations
         *  offline - doing that on a partial array would flicker not-yet-arrived stations offline. **/
        return settleAllProgressive(observables, {label: 'stationStatus', coalesceMs: 600}).pipe(
            map((progress) => {
                const val = progress.results;
                const stations: Array<IStationStatsV1> = [];
                val.forEach((v) => {
                    const source = v.source;
                    /** pwa socket v2 player **/
                    if (v.jData) {
                        v.jData.forEach((station: IStationStatsPropsV2) => {
                            const stationId = station.stationId;
                            const businessId = station.businessId;
                            const stationData: IStationStatsV1V2 = {
                                $: {
                                    playerId: station.playerId,
                                    userHostAddress: station.userHostAddress,
                                    playing: station.playing,
                                    connected: station.connected,
                                    command: station.command,
                                    stationId: station.stationId,
                                    libVersion: station.libVersion,
                                    platform: station.platform,
                                    wdConnected: station.wdConnected,
                                    wdNative: station.wdNative,
                                    station_business_id: concatIdBusinessId(stationId, businessId, 2),
                                    businessId,
                                    id: stationId,
                                    source,
                                    airVersion: '',
                                    localAddress: station.userHostAddress,
                                    localIp: station.userHostAddress,
                                    publicIp: station.userHostAddress,
                                    appVersion: station.appVersion,
                                    caching: '',
                                    cameraStatus: '',
                                    connection: station.connected,
                                    lastCameraTest: '',
                                    os: station.platform,
                                    peakMemory: '',
                                    runningTime: '',
                                    socket: '',
                                    lastUpdate: this.processStationTime(station?.lastUpdate),
                                    startTime: this.processStationTime(station?.startTime),
                                    status: station.connected,
                                    totalMemory: '',
                                    watchDogConnection: station.wdConnected,
                                    version: '2'
                                }
                            };
                            stations.push(stationData);
                        });
                    }
                    /** SignagePlayer legacy players **/
                    if (v.xml) {
                        const xmlData = v.xml.replace(/&lt;/ig, '<').replace(/&gt;/ig, '>');
                        xml2js.parseString(xmlData, (err, result) => {
                            if (err) {
                                console.error('getBusinessDomainCollection xml conversion error');
                                this.onError(this.translocoService.translate('toast.sorryEncounteredProblem', {}, this.translocoService.getActiveLang()));
                            }
                            if (result.string.SocketStatus["0"].Business) {
                                result.string.SocketStatus["0"].Business.forEach((business) => {
                                    const businessId = business.$.businessId;
                                    if (business.Stations["0"].Station) {
                                        business.Stations["0"].Station.forEach((station) => {
                                            const stationId = station.$.id;
                                            // const geoLocation = this.getStationGeoLocation(source, businessId, stationId);
                                            const stationData: IStationStatsV1 = {
                                                $: {
                                                    station_business_id: concatIdBusinessId(station.$.id, businessId, 1),
                                                    businessId,
                                                    id: stationId,
                                                    source,
                                                    airVersion: station.$.airVersion,
                                                    localAddress: '',
                                                    appVersion: station.$.appVersion,
                                                    caching: station.$.caching,
                                                    localIp: station.$.localAddress,
                                                    publicIp: station.$.publicIp,
                                                    cameraStatus: station.$.cameraStatus,
                                                    connection: station.$.connection,
                                                    lastCameraTest: station.$.lastCameraTest,
                                                    lastUpdate: station.$.lastUpdate,
                                                    name: station.$.name,
                                                    os: station.$.os,
                                                    peakMemory: station.$.peakMemory,
                                                    runningTime: station.$.runningTime,
                                                    socket: station.$.socket,
                                                    startTime: station.$.startTime,
                                                    status: station.$.status,
                                                    totalMemory: station.$.totalMemory,
                                                    watchDogConnection: station.$.watchDogConnection,
                                                    version: '1'
                                                }
                                            };
                                            stations.push(stationData);
                                        });
                                    }
                                });
                            }
                        });
                    }
                });
                /** only enrich names once the full set is in, avoids a names refresh storm per chunk **/
                if (progress.done) {
                    this.appService.debounceSyncStationsNamesEnterprise();
                }
                return {stations, isFinal: progress.done};
            }));
    }

    // public getServerPwaAnalytics(): Observable<any> {
    //     const url = 'https://ida.signage.me/Web/getAnalytics.ashx';
    //     return this._http.get<any[]>(url, {
    //         headers: new HttpHeaders({'Content-Type': 'application/json'}), responseType: 'json' as 'json'
    //     }).pipe(
    //         retryWithBackoff(url, this.storeSelector, this.toast),
    //         map((data: any) => {
    //             return data;
    //         })
    //     );
    // }

    public getServerStationsGeoIPs(): Observable<Array<IStationGeoIp>> {
        const stationsIps = [];
        let stationsGeoIps: Array<IStationGeoIp> = [];
        return this.storeSelector.getStationStats().pipe(
            take(1),
            map((i_stations) => {
                i_stations.forEach((i_station) => {
                    const ip = i_station.$.publicIp;
                    const geoLocation = i_station.$.geoLocation;
                    const id = i_station.$.id;
                    const businessId = i_station.$.businessId;
                    const station_business_id = concatIdBusinessId(i_station.$.id, businessId, i_station.$.version);
                    const source = i_station.$.source;
                    // only get stations with public ip and no location info
                    if (!_.isUndefined(ip) && _.isEmpty(geoLocation))
                        stationsIps.push({id, businessId, ip, source, station_business_id});
                });
            }),
            switchMap((v) => {
                const content = JSON.stringify(stationsIps);
                return this._http.post(`${this.coreAppData[1].globalService}/getGeoByIp_v2`,
                    {
                        headers: new HttpHeaders({'Content-Type': 'application/json'}),
                        responseType: 'text' as 'json', content
                    }).pipe(
                    retryWithBackoff(`${this.coreAppData[1].globalService}/getGeoByIp_v2`, this.storeSelector, this.toast),
                    catchError((err) => {
                        console.error('Error loading station IPs geo location');
                        // this.onError(this.translocoService.translate('toast.sorryEncounteredProblem', {}, this.translocoService.getActiveLang()));
                        // return Observable.of(true);
                        return throwError(err);
                    }),
                    finalize(() => {
                        // console.log('done!');
                    }),
                    map((result: any) => {
                        const stations = result;
                        // tslint:disable-next-line:forin
                        for (const station in stations) {
                            const i_station = stations[station];
                            const rand = _.random(0, 30) / 100;
                            i_station.lat = (i_station.lat + rand).toFixed(4);
                            i_station.lon = (i_station.lon + rand).toFixed(4);
                            i_station['city'] = i_station.city;
                            i_station['country'] = i_station.country;
                        }
                        return stationsGeoIps = stations;
                    })
                );
            })
        );
    }

    getStudioProUrl(customerUserName): Observable<string> {
        const url = `${this.coreAppData[0].webApiResellerBaseUrl}&command=GetLoginUrl&customerUserName=${customerUserName}`;
        return this._http.get<any>(url, httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((result: any) => result)
        );
    }

    // getStudioProUrlV2(user, password, customerUserName): Observable<string> {
    //     const d = jsonToBase64({user, password, customerUserName});
    //     const url = `${this.coreAppData[1].globalService}/GetLoginUrl/${customerUserName}`;
    //     return this._http.get<any>(url, httpOptions).pipe(
    //         retryWithBackoff(url, this.storeSelector, this.toast),
    //         map((result: any) => result)
    //     );
    // }

    public getStudioProUrlV2(i_user: IUser, customerUserName: string, server: string): Observable<any> {
        const j = {customerUserName, server};
        const header = getCredentialsHeader(i_user);
        const url = `${this.coreAppData[1].globalService}/SignStudioAutoLogin`;
        return this._http.put<IOrderForm>(url, j, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate('general.problemNetwork', {}, this.translocoService.getActiveLang()));
                return of('problem getStudioProUrlV2 ' + e);
            }),
            map((v: any) => v)
        );
    }

    public resetUserPreferences(i_user: IUser, businessId: Id): Observable<any> {
        const header = getCredentialsHeader(i_user);
        const url = `${this.coreAppData[1].globalService}/resetPreferences`;
        return this._http.put<IOrderForm>(url, {businessId}, header).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            catchError((e) => {
                this.onError(this.translocoService.translate('general.problemNetwork', {}, this.translocoService.getActiveLang()));
                return of('problem getStudioProUrlV2 ' + e);
            }),
            map((v: any) => v)
        );
    }


    getCredentials(customerUserName, returnData?): Observable<string | ICredentialsWrap> {
        const url = `${this.coreAppData[0].webApiResellerBaseUrl}&command=GetUserPass&customerUserName=${customerUserName}`;
        return this._http.get<any>(url, httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((result: any) => {
                if (returnData) return {result, returnData};
                return result;
            })
        );
    }

    changePasswordBusinessUser(userName, userPassword): Observable<string> {
        const url = `${this.coreAppData[0].webApiResellerBaseUrl}&command=ChangePassword&userName=${userName}&newPassword=${Lib.Base64().encode(userPassword)}`;
        return this._http.get<any>(url, httpOptions).pipe(
            retryWithBackoff(url, this.storeSelector, this.toast),
            map((result: any) => result)
        );
    }

    public saveWhiteLabel(action: IEnterpriseWhiteLabelUpdate): Observable<any> {
        const template = Lib.WhiteLabelXmlTemplate(action, false);
        // const url = `${this.coreAppData[0].webApiResellerBaseUrl}&command=SaveWhiteLabel&useWhiteLabel=${action.whitelabelEnabled}&resellerName=${action.companyName}&defaultThemeId=${action.theme}`;
        const url = `${this.coreAppData[0].webApiResellerBaseUrl}&command=SaveWhiteLabel&useWhiteLabel=1&resellerName=${action.companyName}&defaultThemeId=${action.theme}`;
        let httpPost;
        Lib.WhiteLabelXmlTemplate(action, true, (err1, result: IEnterpriseWhiteLabelUpdate) => {
            if (err1) return throwError(err1);
            httpPost = this._http.post(url, template, httpOptions).pipe(
                retryWithBackoff(url, this.storeSelector, this.toast),
                catchError((err2) => {
                    console.error('Error saving whitelabel ' + err2);
                    this.onError(this.translocoService.translate('toast.sorryEncounteredProblem', {}, this.translocoService.getActiveLang()));
                    return throwError(err2);
                }),
                finalize(() => {
                }),
                map((v) => {
                    result.defaultThemeId = action.theme;
                    return result;
                })
            );
        });
        return httpPost;
    }
}
